import { c as _c } from "react/compiler-runtime";
import type { IconProps } from '@phosphor-icons/react';
import { CheckIcon } from '@phosphor-icons/react';
import type { ComponentType, ElementType, PropsWithChildren } from 'react';
import type { VariantProps } from 'tailwind-variants';
import { tv } from 'tailwind-variants';
import type { ButtonAsProps } from '../polymorphic-props';
import { uiTestId } from '../test-ids';
import { Badge } from './badge';
import type { ButtonProps } from './button/button';
import { BaseButton } from './button/button';
export type HeaderItemProps = ButtonProps & {
  checked?: boolean;
  count?: number;
  circle?: boolean;
  label: string;
  Icon: ComponentType<IconProps>;
} & VariantProps<typeof variants>;
const variants = tv({
  slots: {
    base: 'text-primary sm:h-15 relative flex h-12 flex-col items-center rounded-2xl p-3 sm:p-2.5',
    label: 'font-base-copy hidden sm:block',
    count: 'absolute bottom-3.5 left-4',
    checked: 'absolute bottom-3 left-3',
    circle: 'absolute bottom-4 left-5'
  },
  variants: {
    onDarkBg: {
      true: {
        base: 'hover:layer-bg-hover-dark active:layer-bg-active-dark text-white'
      },
      false: {
        base: 'hover:layer-bg-hover-light active:layer-bg-active-light'
      }
    },
    active: {
      true: {
        label: 'font-semibold'
      }
    },
    truncate: {
      true: {
        label: 'max-w-[12ch] truncate sm:max-w-[15ch]'
      }
    }
  }
});
export function HeaderItem(props) {
  const $ = _c(49);
  let Icon;
  let active;
  let ariaLabel;
  let checked;
  let circle;
  let count;
  let label;
  let onDarkBg;
  let ref;
  let rest;
  let truncate;
  if ($[0] !== props) {
    ({
      active,
      checked,
      count,
      circle,
      Icon,
      label,
      onDarkBg,
      ref,
      truncate,
      "aria-label": ariaLabel,
      ...rest
    } = props);
    $[0] = props;
    $[1] = Icon;
    $[2] = active;
    $[3] = ariaLabel;
    $[4] = checked;
    $[5] = circle;
    $[6] = count;
    $[7] = label;
    $[8] = onDarkBg;
    $[9] = ref;
    $[10] = rest;
    $[11] = truncate;
  } else {
    Icon = $[1];
    active = $[2];
    ariaLabel = $[3];
    checked = $[4];
    circle = $[5];
    count = $[6];
    label = $[7];
    onDarkBg = $[8];
    ref = $[9];
    rest = $[10];
    truncate = $[11];
  }
  let T0;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[12] !== Icon || $[13] !== active || $[14] !== ariaLabel || $[15] !== checked || $[16] !== circle || $[17] !== count || $[18] !== label || $[19] !== onDarkBg || $[20] !== ref || $[21] !== rest || $[22] !== truncate) {
    const styles = variants({
      onDarkBg,
      active,
      truncate
    });
    T0 = BaseButton;
    t1 = ariaLabel ?? label;
    t2 = styles.base();
    t3 = ref;
    t4 = rest;
    const t6 = active ? "fill" : "regular";
    let t7;
    if ($[30] !== Icon || $[31] !== t6) {
      t7 = <Icon weight={t6} className="mx-auto size-6" />;
      $[30] = Icon;
      $[31] = t6;
      $[32] = t7;
    } else {
      t7 = $[32];
    }
    const t8 = !!count && <Badge className={styles.count()} testid={uiTestId("HeaderItem.count")} animationKey={count}>{count}</Badge>;
    const t9 = checked && <Badge testid={uiTestId("HeaderItem.checked")} className={styles.checked()} size="small" dark={onDarkBg}><CheckIcon size={12} /></Badge>;
    const t10 = circle && <Badge className={styles.circle()} size="xs" />;
    if ($[33] !== t10 || $[34] !== t7 || $[35] !== t8 || $[36] !== t9) {
      t5 = <div className="relative">{t7}{t8}{t9}{t10}</div>;
      $[33] = t10;
      $[34] = t7;
      $[35] = t8;
      $[36] = t9;
      $[37] = t5;
    } else {
      t5 = $[37];
    }
    t0 = styles.label();
    $[12] = Icon;
    $[13] = active;
    $[14] = ariaLabel;
    $[15] = checked;
    $[16] = circle;
    $[17] = count;
    $[18] = label;
    $[19] = onDarkBg;
    $[20] = ref;
    $[21] = rest;
    $[22] = truncate;
    $[23] = T0;
    $[24] = t0;
    $[25] = t1;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t5;
  } else {
    T0 = $[23];
    t0 = $[24];
    t1 = $[25];
    t2 = $[26];
    t3 = $[27];
    t4 = $[28];
    t5 = $[29];
  }
  let t6;
  if ($[38] !== label || $[39] !== t0) {
    t6 = <span className={t0}>{label}</span>;
    $[38] = label;
    $[39] = t0;
    $[40] = t6;
  } else {
    t6 = $[40];
  }
  let t7;
  if ($[41] !== T0 || $[42] !== t1 || $[43] !== t2 || $[44] !== t3 || $[45] !== t4 || $[46] !== t5 || $[47] !== t6) {
    t7 = <T0 aria-label={t1} className={t2} ref={t3} {...t4}>{t5}{t6}</T0>;
    $[41] = T0;
    $[42] = t1;
    $[43] = t2;
    $[44] = t3;
    $[45] = t4;
    $[46] = t5;
    $[47] = t6;
    $[48] = t7;
  } else {
    t7 = $[48];
  }
  return t7;
}